"use client";

import type { TennisTemplateData } from "../tennis-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type TennisScorecardProps = {
  data: TennisTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function TennisScorecard({
  data,
  participants,
  variant = "feed",
}: TennisScorecardProps) {
  if (!data) return null;

  // Separate teams based on positions
  const yourTeam = participants.filter(
    (p) => data?.positions?.[p.id] === "your-team",
  );
  const opponentTeam = participants.filter(
    (p) => data?.positions?.[p.id] === "opponent-team",
  );

  const showScore = data?.hasScore !== false;
  if (!showScore) return null;

  const yourSets = data?.matchScore?.yourSets ?? 0;
  const opponentSets = data?.matchScore?.opponentSets ?? 0;
  const yourWon = yourSets > opponentSets;
  const opponentWon = opponentSets > yourSets;
  const isDraw = yourSets === opponentSets && yourSets > 0;

  // Get team display names
  const yourTeamName =
    yourTeam.length > 0
      ? yourTeam.map((p) => p.displayName.split(" ")[0]).join(" & ")
      : "Team 1";
  const opponentTeamName =
    opponentTeam.length > 0
      ? opponentTeam.map((p) => p.displayName.split(" ")[0]).join(" & ")
      : "Team 2";

  return (
    <ScorecardShell variant={variant}>
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-text-primary">
            {yourTeamName}
          </span>
          {yourWon && (
            <span className="text-xs font-medium text-brand">Winner</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={
              yourWon
                ? "text-2xl font-bold text-brand"
                : "text-2xl font-bold text-text-primary"
            }
          >
            {yourSets}
          </span>
          <span className="text-text-secondary">–</span>
          <span
            className={
              opponentWon
                ? "text-2xl font-bold text-error"
                : "text-2xl font-bold text-text-primary"
            }
          >
            {opponentSets}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-sm font-semibold text-text-primary">
            {opponentTeamName}
          </span>
          {opponentWon && (
            <span className="text-xs font-medium text-error">Winner</span>
          )}
          {isDraw && (
            <span className="text-xs font-medium text-text-secondary">
              Draw
            </span>
          )}
        </div>
      </div>

      {/* Set details if available */}
      {data?.sets && data.sets.length > 0 && (
        <div className="mt-2 flex justify-center gap-2 border-t border-border pt-2">
          {data.sets.map((set, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: set scores are positional and never reorder
              key={`set-${i}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {set.yourScore}-{set.opponentScore}
            </span>
          ))}
        </div>
      )}
    </ScorecardShell>
  );
}
